import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  PageHeader,
  SegmentedControl,
  Skeleton,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  AlertCircle,
  Copy,
  LayoutTemplate,
  Plus,
  Repeat,
  Search,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  CadenceFormDialog,
  type CadenceFormValues,
} from '@/components/cadences/CadenceFormDialog'
import { useAuth } from '@/hooks/use-auth'
import {
  useCadences,
  useCadenceTemplates,
  useCreateCadence,
  useCreateCadenceFromTemplate,
  useDeleteCadence,
  useDuplicateCadence,
} from '@/hooks/use-cadences'
import { useChannels } from '@/hooks/use-channels'
import { PRO_LIMITS } from '@/types/billing'
import type { Cadence, CadenceStatus } from '@/types/cadences'

export const Route = createFileRoute('/_app/cadencias/')({
  component: CadencesIndexPage,
})

const STATUS_LABEL: Record<CadenceStatus, string> = {
  DRAFT: 'Rascunho',
  ACTIVE: 'Ativa',
  PAUSED: 'Pausada',
  ARCHIVED: 'Arquivada',
}

const STATUS_VARIANT: Record<
  CadenceStatus,
  'default' | 'secondary' | 'outline'
> = {
  DRAFT: 'outline',
  ACTIVE: 'default',
  PAUSED: 'secondary',
  ARCHIVED: 'outline',
}

const STATUS_FILTERS: Array<{ label: string; value: CadenceStatus | 'all' }> = [
  { label: 'Todas', value: 'all' },
  { label: 'Ativas', value: 'ACTIVE' },
  { label: 'Rascunho', value: 'DRAFT' },
  { label: 'Pausadas', value: 'PAUSED' },
  { label: 'Arquivadas', value: 'ARCHIVED' },
]

function CadencesIndexPage() {
  const navigate = useNavigate()
  const { currentOrgId } = useAuth()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<CadenceStatus | 'all'>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Cadence | null>(null)
  const { data: response, isLoading, isError } = useCadences(currentOrgId)
  const { data: templates = [] } = useCadenceTemplates(currentOrgId)
  const { data: channels = [] } = useChannels(currentOrgId)
  const createCadence = useCreateCadence(currentOrgId)
  const createFromTemplate = useCreateCadenceFromTemplate(currentOrgId)
  const duplicateCadence = useDuplicateCadence(currentOrgId)
  const deleteCadence = useDeleteCadence(currentOrgId)

  const cadences = useMemo(() => response?.data ?? [], [response])
  const counts = useMemo(() => {
    const byStatus = cadences.reduce(
      (acc, cadence) => {
        acc[cadence.status] += 1
        return acc
      },
      { DRAFT: 0, ACTIVE: 0, PAUSED: 0, ARCHIVED: 0 } as Record<
        CadenceStatus,
        number
      >
    )
    const enrollments = cadences.reduce(
      (sum, cadence) => sum + (cadence._count?.enrollments ?? 0),
      0
    )
    const steps = cadences.reduce(
      (sum, cadence) => sum + (cadence._count?.steps ?? 0),
      0
    )
    return { byStatus, enrollments, steps }
  }, [cadences])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return cadences.filter((cadence) => {
      if (statusFilter !== 'all' && cadence.status !== statusFilter)
        return false
      if (!query) return true
      return (
        cadence.name.toLowerCase().includes(query) ||
        (cadence.description ?? '').toLowerCase().includes(query)
      )
    })
  }, [cadences, search, statusFilter])

  async function handleCreate(input: CadenceFormValues) {
    const cadence = await createCadence.mutateAsync({
      name: input.name,
      description: input.description || undefined,
      status: 'DRAFT',
      channelStrategy: input.channel_strategy,
      channel_ids: input.channel_ids,
    })
    setDialogOpen(false)
    toast.success('Cadência criada')
    void navigate({
      to: '/cadencias/$cadenceId',
      params: { cadenceId: cadence.id },
    })
  }

  async function handleDelete() {
    if (!deleteTarget) return
    await deleteCadence.mutateAsync(deleteTarget.id)
    toast.success('Cadência excluída')
    setDeleteTarget(null)
  }

  async function handleCreateFromTemplate(templateId: string) {
    const cadence = await createFromTemplate.mutateAsync(templateId)
    setTemplatesOpen(false)
    toast.success('Cadência criada a partir do modelo')
    void navigate({
      to: '/cadencias/$cadenceId',
      params: { cadenceId: cadence.id },
    })
  }

  async function handleDuplicate(cadence: Cadence) {
    const copy = await duplicateCadence.mutateAsync(cadence.id)
    toast.success(`"${cadence.name}" duplicada`)
    void navigate({
      to: '/cadencias/$cadenceId',
      params: { cadenceId: copy.id },
    })
  }

  if (!currentOrgId) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center">
        Selecione uma organização
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={Repeat}
        title="Cadências"
        description="Sequências programadas de mensagens para avançar leads ao longo do tempo"
        meta={<Badge variant="secondary">{response?.total ?? 0}</Badge>}
        actions={
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`text-sm ${counts.enrollments >= (PRO_LIMITS.cadenceEnrollments ?? Infinity) ? 'text-destructive font-medium' : 'text-muted-foreground'}`}
              >
                {counts.enrollments} / {PRO_LIMITS.cadenceEnrollments}{' '}
                inscrições
              </span>
              <Button
                type="button"
                variant="outline"
                onClick={() => setTemplatesOpen(true)}
              >
                <LayoutTemplate className="h-4 w-4" />
                Usar modelo
              </Button>
              <Button type="button" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Nova cadência
              </Button>
            </div>
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-5xl px-6 py-8">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative min-w-64 flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar cadência..."
                className="pl-9"
              />
            </div>
            <SegmentedControl
              label="Status"
              options={STATUS_FILTERS}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </div>

          {isError ? (
            <div className="border-destructive/30 bg-destructive/10 text-destructive flex items-center gap-2 rounded-lg border px-4 py-3 text-sm">
              <AlertCircle className="h-4 w-4" />
              Erro ao carregar cadências.
            </div>
          ) : isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Repeat}
              title={
                cadences.length === 0
                  ? 'Nenhuma cadência ainda'
                  : 'Nada encontrado'
              }
              description={
                cadences.length === 0
                  ? 'Crie uma sequência para programar mensagens automáticas até seus leads.'
                  : 'Ajuste os filtros ou a busca.'
              }
              action={
                cadences.length === 0 ? (
                  <Button onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4" />
                    Criar primeira cadência
                  </Button>
                ) : null
              }
            />
          ) : (
            <ul className="bg-card divide-border border-border divide-y rounded-lg border">
              {filtered.map((cadence) => (
                <li
                  key={cadence.id}
                  className="hover:bg-muted/30 flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors"
                  onClick={() =>
                    void navigate({
                      to: '/cadencias/$cadenceId',
                      params: { cadenceId: cadence.id },
                    })
                  }
                >
                  <div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                    <Repeat className="text-primary h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-base font-medium">
                        {cadence.name}
                      </span>
                      <Badge
                        variant={STATUS_VARIANT[cadence.status]}
                        className="text-xs"
                      >
                        {STATUS_LABEL[cadence.status]}
                      </Badge>
                    </div>
                    {cadence.description ? (
                      <p className="text-muted-foreground mt-0.5 truncate text-sm">
                        {cadence.description}
                      </p>
                    ) : null}
                  </div>
                  <div className="text-muted-foreground hidden text-right text-sm sm:block">
                    <div>
                      <span className="text-foreground font-semibold">
                        {cadence._count?.enrollments ?? 0}
                      </span>{' '}
                      inscrições
                    </div>
                    <div>{cadence._count?.steps ?? 0} etapas</div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={(event) => {
                      event.stopPropagation()
                      void handleDuplicate(cadence)
                    }}
                    title="Duplicar cadência"
                    disabled={duplicateCadence.isPending}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={(event) => {
                      event.stopPropagation()
                      setDeleteTarget(cadence)
                    }}
                    title="Excluir cadência"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <CadenceFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Nova cadência"
        channels={channels.map((ch) => ({ id: ch.id, name: ch.name }))}
        onSubmit={handleCreate}
      />
      <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar cadência a partir de modelo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {templates.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nenhum modelo disponível.
              </p>
            ) : (
              templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => void handleCreateFromTemplate(template.id)}
                  className="hover:bg-muted/40 border-border w-full rounded-md border p-3 text-left transition-colors"
                  disabled={createFromTemplate.isPending}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base font-medium">
                      {template.name}
                    </span>
                    <Badge variant="secondary">
                      {template.stepsCount} etapas
                    </Badge>
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {template.description}
                  </p>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Excluir cadência?"
        description={
          <>
            {deleteTarget?.name} será removida permanentemente, junto com suas
            etapas e inscrições.
          </>
        }
        confirmLabel="Excluir"
        destructive
        pending={deleteCadence.isPending}
        onConfirm={() => void handleDelete()}
      />
    </div>
  )
}
